'use client';

import { useMemo, useState } from 'react';
import {
  HeadingItem,
  IconButton,
  LegacyDialog,
  MetaDefault,
  MonoSmall,
  Spinner,
  Splitter,
  Tree,
  type TreeNode,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  File01Icon,
  Folder01Icon,
  FolderLibraryIcon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  useChatFilesystem,
  type ChatFilesystemFileMeta,
} from '../../../hooks/api/useChatFilesystem';
import { LayoutSplitterResizeTrigger } from '../../layout/LayoutSplitterResizeTrigger';
import { ChatFilesystemFilePane } from './ChatFilesystemFilePane';

const buildTree = (
  files: ChatFilesystemFileMeta[],
): TreeNode<ChatFilesystemFileMeta>[] => {
  const nodes: TreeNode<ChatFilesystemFileMeta>[] = [];
  const pathMap = new Map<string, TreeNode<ChatFilesystemFileMeta>>();

  for (const file of files) {
    const normalized = file.path.startsWith('/')
      ? file.path.slice(1)
      : file.path;
    const parts = normalized.split('/').filter(Boolean);
    let currentPath = '';
    let parentNode: TreeNode<ChatFilesystemFileMeta> | undefined;

    parts.forEach((part, index) => {
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      const isLeaf = index === parts.length - 1;

      if (!pathMap.has(currentPath)) {
        const node: TreeNode<ChatFilesystemFileMeta> = {
          children: [],
          data: isLeaf ? file : undefined,
          icon: isLeaf ? (
            <File01Icon size={14} color="var(--colors-fg-muted)" />
          ) : (
            <Folder01Icon size={14} color="var(--colors-fg-muted)" />
          ),
          id: currentPath,
          isExpanded: true,
          level: index,
          name: part,
          parent: parentNode,
        };

        pathMap.set(currentPath, node);
        nodes.push(node);
      }

      parentNode = pathMap.get(currentPath);
    });
  }

  return nodes;
};

export function ChatFilesystemViewer({
  chatId,
  applicationId,
  isOpen,
  onClose,
}: {
  chatId: string;
  applicationId?: string | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  const t = useTranslations('chat.copilot.agentContext');
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);
  const { data: files = [], isLoading } = useChatFilesystem(
    chatId,
    isOpen,
    applicationId,
  );

  const treeData = useMemo(() => buildTree(files), [files]);

  const handleSelectionChange = (selectedNodeIds: string[]) => {
    setSelectedNodes(selectedNodeIds);
    const lastId = selectedNodeIds[selectedNodeIds.length - 1];
    const node = treeData.find((candidate) => candidate.id === lastId);

    if (node?.data) {
      setSelectedPath(node.data.path);
    }
  };

  const renderNode = (node: TreeNode<ChatFilesystemFileMeta>) =>
    node.data ? (
      <MetaDefault color="fg.default" truncate title={node.name}>
        {node.name}
      </MetaDefault>
    ) : (
      <MonoSmall color="fg.muted" truncate title={node.name}>
        {node.name}
      </MonoSmall>
    );

  return (
    <LegacyDialog.Root
      open={isOpen}
      onOpenChange={({ open }) => !open && onClose()}
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content
          maxW="92vw"
          w="92vw"
          h="88vh"
          display="flex"
          flexDirection="column"
          p={0}
          overflow="hidden"
          bg="bg.default"
        >
          <HStack
            px="4"
            py="3"
            borderBottomWidth="1px"
            borderColor="border.subtle"
            justifyContent="space-between"
            alignItems="center"
            bg="bg.subtle"
            flexShrink={0}
          >
            <HStack gap="3" alignItems="center" minW="0">
              <Box
                p="2"
                borderRadius="md"
                bg="bg.canvas"
                color="fg.muted"
                display="flex"
                borderWidth="1px"
                borderColor="border.subtle"
              >
                <FolderLibraryIcon size={16} />
              </Box>
              <VStack alignItems="flex-start" gap="0.5" minW="0">
                <HeadingItem color="fg.default">
                  {t('filesystemHubLabel')}
                </HeadingItem>
                <MonoSmall color="fg.muted" truncate>
                  {t('filesystemHubSubtitle')}
                </MonoSmall>
              </VStack>
              <MetaDefault color="fg.subtle" fontFamily="mono">
                {t('filesystemFilesCount', { count: files.length })}
              </MetaDefault>
            </HStack>
            <LegacyDialog.CloseTrigger asChild>
              <IconButton
                type="button"
                aria-label={t('closeAriaLabel')}
                variant="ghost"
                size="xs"
              >
                <Cancel01Icon size={16} />
              </IconButton>
            </LegacyDialog.CloseTrigger>
          </HStack>

          <Splitter.Root
            orientation="horizontal"
            flex="1"
            minH="0"
            h="100%"
            panels={[
              { id: 'tree', minSize: 18 },
              { id: 'preview', minSize: 40 },
            ]}
            defaultSize={[28, 72]}
            borderWidth="0"
            gap={0}
          >
            <Splitter.Panel
              id="tree"
              borderWidth="0"
              borderRadius="none"
              alignItems="stretch"
              justifyContent="flex-start"
              bg="bg.subtle"
              overflow="hidden"
            >
              {isLoading ? (
                <HStack justifyContent="center" alignItems="center" h="full">
                  <Spinner size="sm" />
                </HStack>
              ) : files.length === 0 ? (
                <VStack
                  justifyContent="center"
                  alignItems="center"
                  h="full"
                  px="4"
                >
                  <MonoSmall color="fg.subtle" textAlign="center">
                    {t('filesystemEmpty')}
                  </MonoSmall>
                </VStack>
              ) : (
                <Box
                  h="full"
                  overflowY="auto"
                  overflowX="hidden"
                  p="2"
                  minH="0"
                >
                  <Tree
                    data={treeData}
                    selectedNodes={selectedNodes}
                    onSelectionChange={handleSelectionChange}
                    fullWidth
                    selectionMode="single"
                    title=""
                    searchPlaceholder={t('filesystemSearchPlaceholder')}
                    emptyMessage={t('filesystemNoResults')}
                    renderNode={renderNode}
                  />
                </Box>
              )}
            </Splitter.Panel>

            <LayoutSplitterResizeTrigger id="tree:preview" />

            <Splitter.Panel
              id="preview"
              borderWidth="0"
              borderRadius="none"
              alignItems="stretch"
              justifyContent="flex-start"
              overflow="hidden"
              minH="0"
            >
              <ChatFilesystemFilePane
                key={selectedPath ?? 'empty'}
                chatId={chatId}
                applicationId={applicationId}
                path={selectedPath}
                onSelectPath={setSelectedPath}
              />
            </Splitter.Panel>
          </Splitter.Root>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
}
